import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  Play,
  Landmark,
  MapPin,
  Scale,
  Building,
  Flag,
  Users,
  Globe,
  Shield,
  Palette,
  FileText
} from "lucide-react";

const categoryIcons = {
  histoire: Landmark,
  geographie: MapPin,
  valeurs: Scale,
  institutions: Building,
  symboles: Flag,
  culture: Palette,
  europe: Globe,
  droits: Shield,
  societe: Users,
  demarches: FileText
};

const categoryNames = {
  histoire: "Histoire de France",
  geographie: "Géographie",
  valeurs: "Valeurs républicaines",
  institutions: "Institutions",
  symboles: "Symboles nationaux",
  culture: "Culture et patrimoine",
  europe: "Union Européenne",
  droits: "Droits et devoirs",
  societe: "Vie en société",
  demarches: "Démarches administratives"
};

const FicheDetail = () => {
  const { ficheId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [fiche, setFiche] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFiche = async () => {
      try {
        const response = await axios.get(`${API}/fiche/${ficheId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFiche(response.data);
      } catch (error) {
        toast.error("Fiche non trouvée");
        navigate("/fiches");
      } finally {
        setLoading(false);
      }
    };
    fetchFiche();
  }, [ficheId, token, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!fiche) return null;

  const Icon = categoryIcons[fiche.category] || BookOpen;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fiche-detail-page">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate("/fiches")}
          className="mb-6 text-slate-600"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux fiches
        </Button>

        {/* Header */}
        <div className="mb-8">
          <span className={`category-badge ${fiche.category} mb-4 inline-flex`}>
            <Icon className="h-3 w-3" />
            {categoryNames[fiche.category] || fiche.category}
          </span>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-slate-900">
            {fiche.title}
          </h1>
        </div>

        {/* Content */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <div className="prose prose-slate max-w-none">
              {fiche.content.split('\n\n').map((paragraph, idx) => (
                <div key={idx} className="mb-4">
                  {paragraph.startsWith('**') ? (
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {paragraph.replace(/\*\*/g, '')}
                    </h3>
                  ) : paragraph.startsWith('- ') ? (
                    <ul className="list-disc list-inside space-y-1 text-slate-600">
                      {paragraph.split('\n').map((item, i) => (
                        <li key={i}>{item.replace('- ', '')}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-600 leading-relaxed">{paragraph}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Key Points */}
        <Card className="mb-8 bg-blue-50 border-blue-100">
          <CardContent className="p-6">
            <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-blue-900" />
              Points clés à retenir
            </h3>
            <ul className="space-y-3">
              {fiche.key_points.map((point, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="h-6 w-6 bg-blue-900 text-white rounded-full flex items-center justify-center text-sm font-medium shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-slate-700">{point}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate("/fiches")}
          >
            <BookOpen className="h-4 w-4 mr-2" />
            Autres fiches
          </Button>
          <Button
            className="flex-1 bg-blue-900 hover:bg-blue-800"
            onClick={() => navigate(`/quiz/${fiche.category}`)}
            data-testid="start-quiz-btn"
          >
            <Play className="h-4 w-4 mr-2" />
            Quiz sur ce thème
          </Button>
        </div>
      </main>
    </div>
  );
};

export default FicheDetail;
